/**
 * useBusinessContext — business-context CRUD + regenerate hooks.
 *
 * Three hooks in one module since they all hit the same query key
 * (``enterpriseBusinessContextKey``) and the consumers (BusinessContextEditor)
 * always import the three together. The query intentionally reads from
 * the connector detail endpoint via the BE — the BE exposes
 * ``business_context`` on ``GET /enterprise/connectors/{id}/profiling-status``
 * (it tags along with the discovery snapshot) and on
 * ``POST /regenerate-context``. We expose a thin helper that reads the
 * profiling status (already cached by ``useProfilingStatus``) so the
 * editor doesn't fire a second round-trip just for the text.
 *
 * Update + regenerate mutations always invalidate the profiling cache
 * so the displayed text reconciles with the BE truth.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  type ProfilingStatusResponse,
  type RegenerateBusinessContextResponse,
  getEnterpriseProfilingStatus,
  regenerateEnterpriseBusinessContext,
  updateEnterpriseBusinessContext,
} from "@ando/core/api";

import { enterpriseBusinessContextKey, enterpriseProfilingKey } from "./enterpriseKeys";

const STALE_MS = 60_000;

export interface BusinessContextSnapshot {
  readonly business_context: string;
  readonly updated_at: string | null;
}

/**
 * Query — read the current business context. Derived from the profiling
 * status snapshot (BE exposes ``business_context`` there). Returning a
 * dedicated cache key (not the profiling key) decouples the editor's
 * staleness model from the polling intervals of the discovery dialog.
 */
export function useBusinessContextQuery(connId: string, enabled = true) {
  return useQuery<BusinessContextSnapshot>({
    queryKey: enterpriseBusinessContextKey(connId),
    queryFn: async () => {
      const status: ProfilingStatusResponse = await getEnterpriseProfilingStatus(connId);
      return {
        business_context: status.business_context ?? "",
        // BE doesn't expose a dedicated updated_at on this slice — the
        // editor renders "Ultimo aggiornamento {now}" on successful save
        // instead of relying on this value.
        updated_at: null,
      };
    },
    enabled: enabled && connId.length > 0,
    staleTime: STALE_MS,
    retry: 1,
  });
}

export interface UseUpdateBusinessContextVars {
  readonly connId: string;
  readonly businessContext: string;
}

export function useUpdateBusinessContext() {
  const qc = useQueryClient();
  return useMutation<void, Error, UseUpdateBusinessContextVars>({
    mutationFn: ({ connId, businessContext }) =>
      updateEnterpriseBusinessContext(connId, businessContext),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: enterpriseBusinessContextKey(vars.connId),
      });
      void qc.invalidateQueries({
        queryKey: enterpriseProfilingKey(vars.connId),
      });
    },
  });
}

export interface UseRegenerateBusinessContextVars {
  readonly connId: string;
  /** See ``RegenerateBusinessContextOptions.force``. */
  readonly force?: boolean;
}

export function useRegenerateBusinessContext() {
  const qc = useQueryClient();
  return useMutation<RegenerateBusinessContextResponse, Error, UseRegenerateBusinessContextVars>({
    mutationFn: ({ connId, force }) => regenerateEnterpriseBusinessContext(connId, { force }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: enterpriseBusinessContextKey(vars.connId),
      });
      void qc.invalidateQueries({
        queryKey: enterpriseProfilingKey(vars.connId),
      });
    },
  });
}
